import { useMemo } from "react"

import { Popover, Text } from "@/atoms"
import {
  getApplicationSourceDisplay,
  getResourceDrawer,
  getResourcesNounKey,
  getStandInApplicationId
} from "@/helpers/application-source.helper"
import {
  useLinkDrawerTooltip,
  type RowClick
} from "@/hooks/useLinkDrawerTooltip"
import { ImageIcon, VmIcons } from "@/icons"
import { IconTextCell } from "@/molecules/IconTextCell"
import { IconResource } from "@/molecules/Resources"
import { CODE_REPOSITORY_DRAWER_ID } from "@/organisms/Drawers/const"
import { QuickWinProjectBadge } from "../QuickWinProjectBadge"
import { Resource } from "./Resource"

import styles from "./ResourceCell.module.scss"

import type { ResourceCellProps } from "."

type KeyOf<T> = keyof T

function uniqueByField<T>(array: T[], ...field: KeyOf<T>[]): T[] {
  const seen = new Map<string, boolean>()
  return array.reduce((acc, item) => {
    const compositeKey = field.map((f) => item[f]).join()
    if (!seen.has(compositeKey)) {
      seen.set(compositeKey, true)
      acc.push(item)
    }
    return acc
  }, [] as T[])
}

export const ResourceWithPopover = (
  props: ResourceCellProps & { valueCell: string; onRowClick: RowClick }
) => {
  const {
    valueCell,
    resources,
    sourceControl,
    relatedImagesCount,
    relatedVMsCount,
    quickWinsCount,
    quickWinsTotalIssues,
    id,
    t
  } = props
  const isRepoResource = !!sourceControl
  const relatedResource = resources?.[1]
  const imageSourceRelatedResource = relatedResource?.imageSource
  const showRelatedImages =
    isRepoResource && !!relatedImagesCount && !!imageSourceRelatedResource
  const showRelatedVMs = isRepoResource && !!relatedVMsCount
  const showRelatedResource = showRelatedImages || showRelatedVMs
  const { open, openDrawer } = useLinkDrawerTooltip(props)
  const {
    type: typeFirstRResource,
    imageSource,
    applicationSource
  } = resources[0] || {}
  const type = isRepoResource ? "CodeRepository" : typeFirstRResource
  const RelatedApplicationIcon =
    getApplicationSourceDisplay(relatedResource)?.Icon
  const relatedImageIcon = RelatedApplicationIcon ? (
    <RelatedApplicationIcon />
  ) : (
    showRelatedImages && <ImageIcon imageSource={imageSourceRelatedResource} />
  )
  const relatedImagesNounKey = getResourcesNounKey(
    resources.slice(1),
    "general.image"
  )
  const uniqueResources = useMemo(
    () => uniqueByField(resources, "id", "path"),
    [resources]
  )
  return (
    <div className={styles.container}>
      <Popover
        open={open}
        rootClassName={styles.root}
        content={
          <div className={styles.tooltipWrapper}>
            {uniqueResources.map((resource, index) => {
              const isCodeRepo = resource.type === "CodeRepository"
              // A resource standing in for an application opens the application drawer
              const isApplication = !!getStandInApplicationId(resource)
              if (!isCodeRepo && !isApplication) {
                return (
                  <Text key={index}>
                    <Resource key={index} {...resource} />
                  </Text>
                )
              }
              const { drawerId, key } = getResourceDrawer(
                resource,
                CODE_REPOSITORY_DRAWER_ID
              )
              return (
                <Text
                  underline
                  key={index}
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    openDrawer?.(drawerId, key)
                  }}
                >
                  <Resource key={index} {...resource} name={resource.name} />
                </Text>
              )
            })}
          </div>
        }
      >
        <span className={styles.labelWrapper}>
          <IconTextCell
            noTooltip
            icon={
              <IconResource
                type={type}
                sourceControl={sourceControl}
                imageSource={imageSource}
                applicationSource={
                  isRepoResource ? undefined : applicationSource
                }
              />
            }
            text={valueCell}
          />
          {showRelatedResource && (
            <IconTextCell
              noTooltip
              icon={
                showRelatedImages ? (
                  relatedImageIcon
                ) : (
                  <VmIcons type={relatedResource?.subjectType} />
                )
              }
              text={`+${showRelatedImages ? relatedImagesCount : relatedVMsCount} related ${showRelatedImages ? t(relatedImagesNounKey, { count: relatedImagesCount }) : t("general.vm", { count: relatedVMsCount })}`}
              classes={{ container: styles.relatedResourceContainer }}
            />
          )}
        </span>
      </Popover>
      {!!quickWinsCount && (
        <QuickWinProjectBadge
          count={quickWinsCount}
          projectId={id}
          totalIssues={quickWinsTotalIssues ?? 0}
        />
      )}
    </div>
  )
}
